import { Suspense, lazy } from "react"

import {
  ClientOnly,
  FieldError,
  Heading,
  Icon,
  Spinner,
} from "@trueskin-web/components"
import { dragAndDropIcon } from "@trueskin-web/theme/icons/dragAndDropIcon"
import i18next, { Trans } from "@trueskin-web/translations"

import { usePhotos } from "./image-upload-hooks"
import PhotoSlot from "./photo-slot"

const SortablePhotos = lazy(() => import("./sortable-photos"))

const PhotoSlots = ({
  className,
  isModern,
  heading,
  showDescription,
  showTexts = true,
  showPhotoPlaceholders,
  centerActions,
  texts = {
    front: i18next.t("PhotoSlots.positions.front"),
    right: i18next.t("PhotoSlots.positions.right"),
    left: i18next.t("PhotoSlots.positions.left"),
  },
}) => {
  const {
    photos,
    photosOrder,
    positions,

    handleAdd,
    handleAddMultiple,
    handleRemove,
    handleSortEnd,

    isSkipUploadLoading,
    isUploadImagesLoading,
    uploadError,
  } = usePhotos()

  return (
    <div data-testid="web-portal-photos-slots">
      <div className={className} sx={{ position: "relative" }}>
        {heading && (
          <Heading
            as="h3"
            mb={6}
            sx={{ fontSize: [4, 6], variant: "vwo.heading" }}
          >
            {heading}
          </Heading>
        )}

        <div sx={{ position: "relative", mb: [3, 4] }}>
          <div
            sx={{
              display: "flex",
              mx: [-1, -2],
              pointerEvents: "none",
            }}
          >
            {positions.map((position) => (
              <PhotoSlot
                key={position}
                position={position}
                showPhotoPlaceholders={showPhotoPlaceholders}
                isModern={isModern}
              />
            ))}
          </div>

          <ClientOnly>
            <Suspense fallback={<div />}>
              <SortablePhotos
                isModern={isModern}
                photos={photos}
                axis="x"
                onSortEnd={handleSortEnd}
                centerActions={centerActions}
                // hack needed for firefox
                // https://github.com/clauderic/react-sortable-hoc/issues/253
                // another option https://github.com/clauderic/react-sortable-hoc/issues/321#issuecomment-354730170
                onSortStart={(_, event) => event.preventDefault()}
                onAdd={handleAdd}
                onAddMultiple={handleAddMultiple}
                onRemove={handleRemove}
              />
            </Suspense>
          </ClientOnly>
        </div>

        {showTexts && (
          <div
            sx={{
              display: "flex",
              mx: [-1, -2],
              textAlign: "center",
            }}
          >
            <div
              sx={{
                flex: 1,
                mx: [1, 2],
                order: photosOrder.front,
              }}
            >
              {texts.front}
            </div>
            <div
              sx={{
                flex: 1,
                mx: [1, 2],
                order: photosOrder.right,
              }}
            >
              {texts.right}
            </div>
            <div
              sx={{
                flex: 1,
                mx: [1, 2],
                order: photosOrder.left,
              }}
            >
              {texts.left}
            </div>
          </div>
        )}

        {showDescription && (
          <div sx={{ display: "flex", alignItems: "center", mt: [6, 7] }}>
            <Icon
              icon={dragAndDropIcon}
              color="primary"
              sx={{ width: [45, 55], height: [45, 55], ml: 2, mr: 3 }}
            />
            <div sx={{ fontSize: [1, 4], lineHeight: 1.2, color: "primary" }}>
              <Trans i18nKey="PhotoSlots.instructions" />
            </div>
          </div>
        )}

        {(isSkipUploadLoading || isUploadImagesLoading) && (
          <div
            sx={{
              display: "grid",
              placeItems: "center",
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "rgba(255, 255, 255, 0.85)",
            }}
          >
            <Spinner />
          </div>
        )}
      </div>

      <div>
        {uploadError && (
          <FieldError sx={{ mt: 4 }}>Error {uploadError.message}</FieldError>
        )}
      </div>
    </div>
  )
}

export default PhotoSlots
